import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"

import type {
  AlertSetting,
  AlertSettingType,
  AlertSettingUpsertForm,
  DeviceAlertSetting,
} from "types/alerts.types"
import type { EntityType } from "types/common.types"
import { getTelemetryWithUnit } from "helpers/utils/telemetries"
import { reverseFormatThresholds } from "helpers/utils/alerts"
import { numberToLocale } from "helpers/utils/localeNumber"
import UpdateDeviceAlertSetting from "./device/UpdateDeviceAlertSetting"
import UpdatePowerlineAlertSetting from "./powerline/UpdatePowerlineAlertSetting"
import UpdateTowerAlertSetting from "./tower/UpdateTowerAlertSetting"
import { getNewAlertSettingDefaultValues } from "./thresholds"

interface UpdateAlertSettingProps {
  selectedAlert: AlertSetting
  onClose: () => void
  types: AlertSettingType[]
  entity: EntityType
}

const UpdateAlertSetting = ({
  selectedAlert,
  onClose,
  types,
  entity,
}: UpdateAlertSettingProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage || "en"
  const selectedType = types?.find((tp) => tp.name === selectedAlert.type)
  // Params missing from the saved config (e.g. note) start like in a new alert,
  // so the form isn't dirty before anything is touched.
  const defaultValues: {
    type: string
    [key: string]: Array<any> | boolean | number | string | null
  } = {
    ...getNewAlertSettingDefaultValues(selectedAlert.type, selectedType),
    type: selectedAlert.type,
    ...selectedAlert.config,
  }
  const fields = selectedAlert.config.fields as string[]
  defaultValues.fields = fields?.map((field) => {
    return {
      label: getTelemetryWithUnit(field, t),
      value: field,
    }
  })

  if (defaultValues.thresholds && selectedAlert.config.thresholds) {
    const formattedThresholds = reverseFormatThresholds(
      selectedAlert.config.thresholds,
      locale,
    )
    defaultValues.thresholds = formattedThresholds
  }

  // Format numeric params (e.g. auto_resolve_timeout) for locale display
  if (selectedType) {
    for (const param of selectedType.params) {
      if (param.type === "float" && typeof defaultValues[param.id] === "number") {
        defaultValues[param.id] =
          numberToLocale(defaultValues[param.id] as number, locale) ?? ""
      }
    }
  }

  const methods = useForm<AlertSettingUpsertForm>({
    mode: "all",
    defaultValues,
  })

  // Not memoized: methods never changes, so a memo would hide formState updates
  // (isDirty) from the form below.
  switch (entity) {
    case "powerline":
      return (
        <UpdatePowerlineAlertSetting
          selectedAlert={selectedAlert}
          onClose={onClose}
          types={types}
          methods={methods}
        />
      )
    case "tower":
      return (
        <UpdateTowerAlertSetting
          selectedAlert={selectedAlert}
          onClose={onClose}
          types={types}
          methods={methods}
        />
      )
    case "device":
    default:
      return (
        <UpdateDeviceAlertSetting
          selectedAlert={selectedAlert as DeviceAlertSetting}
          onClose={onClose}
          types={types}
          methods={methods}
        />
      )
  }
}

export default UpdateAlertSetting
